import {
  AlignItems,
  Body2,
  Body3,
  Bold,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading2,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import React from 'react'

type OrderTilePriceProps = {
  currentPrice: string | null
  originalPrice: string | null
}

const OrderTilePrice = ({ currentPrice, originalPrice }: OrderTilePriceProps) => (
  <Box display={Display.Flex} flexDirection={FlexDirection.Column} alignItems={AlignItems.FlexEnd}>
    {originalPrice ? (
      <Text
        size={0}
        display={Display.Inline}
        textAlign={TextAlign.Right}
        color={Color.ColdGrey_600}
      >
        <s>{originalPrice}</s>
      </Text>
    ) : null}
    <Space size={0} direction="vertical" />
    {currentPrice ? (
      <Text
        size={[4, 9]}
        display={Display.Inline}
        textAlign={TextAlign.Right}
        color={Color.ColdGrey_800}
      >
        <Bold>{currentPrice}</Bold>
      </Text>
    ) : null}
  </Box>
)

const ManageDeliveryChevronButton = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

type MobileManageDeliveryToggleProps = {
  isManageDeliveryExpanded: boolean
  onToggle: () => void
}

const MobileManageDeliveryToggle = ({
  isManageDeliveryExpanded,
  onToggle,
}: MobileManageDeliveryToggleProps) => (
  <ManageDeliveryChevronButton
    role="button"
    tabIndex={0}
    aria-label="Toggle manage delivery details"
    aria-expanded={isManageDeliveryExpanded}
    aria-controls="manage-delivery-panel"
    display={['flex', 'flex', 'none']}
    alignItems={AlignItems.Center}
    justifyContent={JustifyContent.FlexEnd}
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onToggle()
      }
    }}
  >
    <Icon
      name={isManageDeliveryExpanded ? 'chevron_up' : 'chevron_down'}
      variant={IconVariant.Actionable}
      size={8}
    />
  </ManageDeliveryChevronButton>
)

type OrderTileHeaderProps = {
  currentPrice: string | null
  isManageDeliveryExpanded: boolean
  onManageDeliveryClick: () => void
  order: DashboardExperienceOrderResponse
  originalPrice: string | null
}

export const OrderTileHeader = ({
  currentPrice,
  isManageDeliveryExpanded,
  onManageDeliveryClick,
  order,
  originalPrice,
}: OrderTileHeaderProps) => (
  <Box
    width="100%"
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    alignItems={AlignItems.FlexStart}
  >
    <Box
      width="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Row}
      justifyContent={JustifyContent.SpaceBetween}
      alignItems={AlignItems.FlexStart}
    >
      <Box
        display={['flex', 'flex', 'none']}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.FlexStart}
        gap={1}
      >
        <Heading2 size={[3, 5]}>{order.title}</Heading2>
        <Body2 color={Color.ColdGrey_600} fontWeight={FontWeight.Bold}>
          {order.state_title}
        </Body2>
      </Box>
      <Box
        display={['none', 'none', 'flex']}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.FlexStart}
        gap={1}
      >
        <Heading2 size={[3, 5]}>{order.title}</Heading2>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          alignItems={AlignItems.Center}
          gap={3}
        >
          <Body2 color={Color.ColdGrey_600} fontWeight={FontWeight.Bold}>
            {order.state_title}
          </Body2>
          <Body3 color={Color.ColdGrey_800}>|</Body3>
          <Body3 color={Color.ColdGrey_800}>{order.summary_text}</Body3>
        </Box>
      </Box>
      <OrderTilePrice currentPrice={currentPrice} originalPrice={originalPrice} />
    </Box>
    <Space size={1} direction="vertical" />
    <Box
      display={['flex', 'flex', 'none']}
      width="100%"
      flexDirection={FlexDirection.Row}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.SpaceBetween}
      gap={3}
    >
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        alignItems={AlignItems.Center}
        gap={3}
      >
        <Body3 color={Color.ColdGrey_800}>{order.summary_text}</Body3>
      </Box>
      <MobileManageDeliveryToggle
        isManageDeliveryExpanded={isManageDeliveryExpanded}
        onToggle={onManageDeliveryClick}
      />
    </Box>
  </Box>
)
